import { Grid } from '@material-ui/core';
import { type ICourseReport } from 'src/models';
import { NoResults, ReportButtons } from 'src/components';
import React, { useEffect, useState } from 'react';

import CourseCard from '../CourseCard';
import CourseChart from '../CourseChart';
import QualificationsReportDialog from '../QualificationsReportDialog';

import { type RootState } from 'src/store';
import { useDispatch, useSelector } from 'react-redux';
import courseActions from 'src/store/Courses/actions';

function Admin() {
	// NOTE:
	// Para mayor control de las consultas a la API recomendamos usar 2 estados en cada página:
	// isError e isLoading. Con estas variables de control podemos estar seguros de que la página
	// no se cargará más de las veces necesarias.
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const dispatch = useDispatch();
	// NOTE:
	// La documentación de redux recomienda "memoizar" todas las funciones de useSelector
	// para optimizaciones de memoria.
	// https://react-redux.js.org/api/hooks#performance
	const reports = useSelector(
		(state: RootState) => state.courses.reports.all,
	);

	// Const getReport = () => dispatch(courseActions.getReport('all'));
	const [reportOpen, setReportOpen] = useState(false);

	// NOTE:
	// La referencia de la función de dispatch será estable siempre que la misma instancia
	// de store se pase al <Provider>. Normalmente, esa instancia de store nunca
	// cambia en una aplicación. Sin embargo, las reglas de lint de hooks de React no saben
	// que el dispatch debe ser estable y advertirán que la dispatch de envío debe agregarse a las
	// matrices de dependencia para useEffect y useCallback. La solución más simple es hacer
	// precisamente eso:

	useEffect(() => {
		// NOTE:
		// Podemos hacer try/catch para intentar observar algún tipo de error y
		// reportar al usuario de que hay algo mal con el proceso de consulta
		try {
			if (isLoading) {
				dispatch(courseActions.getReport('all'));
			}
		} catch (error) {
			console.error(error);
			setIsError(true);
		} finally {
			setIsLoading(false);
		}
	}, [dispatch]);

	// NOTE:
	// Recomendamos atrapar el status code (4x, 5x, etc.) que la API devuelve y mostrar un componente
	// de error acorde al problema, con el propósito de que nos puedan reportar el error y podamos
	// identificarlo.
	if (isError) return <>Error...</>;

	// NOTE:
	// Recomendamos hacer un componente que muestre un skeleton con más detalle,
	// para que el usuario sepa como se verá el resultado de la consulta.
	// https://mui.com/material-ui/react-skeleton/
	if (isLoading) return <>Loading...</>;

	// NOTE:
	// Se deben de declarar justo después de isError e IsLoading.
	// En el caso de tener funciones, variables o constantes que se tengan que declarar.

	// NOTE:
	// reports.data?.length === 0 devolvería un falso positivo cuando este undefined,
	// > const x = {}
	// > x.foo?.length === 0
	// false
	// recomendamos usar el operador OR para declarar una variable primero, luego determinar
	// la longitud de la misma:
	// > const bar = x?.foo || []
	// > bar.length === 0
	// true
	const data = reports?.data || [];

	return (
		<Grid container>
			<QualificationsReportDialog
				handleOpen={() => {
					setReportOpen(!reportOpen);
				}}
				open={reportOpen}
			/>

			<ReportButtons
				handleOpen={() => {
					setReportOpen(!reportOpen);
				}}
				type={'all'}
			/>

			{data.length === 0 && <NoResults text={'Aún no hay información'} />}

			<Grid container spacing={2} style={{ marginBottom: 24 }}>
				{reports.data.map((courseReport: ICourseReport) => (
					<CourseCard
						courseReport={courseReport}
						key={courseReport.id}
						type={'all'}
					/>
				))}
			</Grid>

			<CourseChart type={'all'} />
		</Grid>
	);
}

export default Admin;
